import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import axios from "api/axios";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PageShell, { SectionCard } from "components/patch/PageShell";
import SimpleTable from "components/patch/SimpleTable";

const statusColor = (status) => {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "processing") return "info";
  if (status === "cancelled") return "warning";
  return "default";
};

export default function PayrollPerformance() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [runId, setRunId] = useState("");
  const [reportType, setReportType] = useState("items");
  const [chunkSize, setChunkSize] = useState(500);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadJobs = async () => {
    const res = await axios.get("/payroll-performance/jobs", { params: { per_page: 20 } });
    setJobs(res.data?.data || []);
  };

  useEffect(() => {
    loadJobs();
    const timer = setInterval(loadJobs, 8000);
    return () => clearInterval(timer);
  }, []);

  const queueGeneration = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await axios.post("/payroll-performance/generate-payroll", {
        month,
        chunk_size: Number(chunkSize || 500),
      });
      setMessage({ type: "success", text: res.data?.message || "Payroll generation queued." });
      await loadJobs();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Unable to queue payroll.",
      });
    } finally {
      setLoading(false);
    }
  };

  const queueExport = async () => {
    if (!runId) {
      setMessage({ type: "warning", text: "Enter a payroll run ID first." });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const res = await axios.post(`/payroll-performance/runs/${runId}/export`, {
        report_type: reportType,
        chunk_size: 1000,
      });
      setMessage({ type: "success", text: res.data?.message || "Report export queued." });
      await loadJobs();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Unable to queue export." });
    } finally {
      setLoading(false);
    }
  };

  const downloadJob = (job) => {
    window.open(`${axios.defaults.baseURL}/payroll-performance/jobs/${job.id}/download`, "_blank");
  };

  const cancelJob = async (job) => {
    setLoading(true);
    try {
      await axios.post(`/payroll-performance/jobs/${job.id}/cancel`);
      await loadJobs();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Unable to cancel job." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Payroll Performance & Queues"
      subtitle="Run large payroll computations and exports safely in the background. Use this page for large staff volumes, heavy reports, and production payroll processing."
    >
      <Grid container spacing={2}>
        {message ? (
          <Grid item xs={12}>
            <Alert severity={message.type}>{message.text}</Alert>
          </Grid>
        ) : null}

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Queue Payroll Generation"
            subtitle="Recommended for production payroll runs and large staff counts."
          >
            <Stack spacing={2}>
              <MDInput
                label="Payroll Month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                fullWidth
              />
              <MDInput
                label="Chunk Size"
                type="number"
                value={chunkSize}
                onChange={(e) => setChunkSize(e.target.value)}
                fullWidth
                helperText="Use 300–500 for shared hosting, 1000–2000 for stronger servers."
              />
              <MDButton color="info" onClick={queueGeneration} disabled={loading || !month}>
                Queue Payroll Generation
              </MDButton>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Queue Large Report Export"
            subtitle="Exports CSV files without browser timeout or server memory spikes."
          >
            <Stack spacing={2}>
              <MDInput
                label="Payroll Run ID"
                value={runId}
                onChange={(e) => setRunId(e.target.value)}
                fullWidth
              />
              <MDInput
                select
                label="Report Type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                fullWidth
              >
                <MenuItem value="items">Payroll Items</MenuItem>
                <MenuItem value="summary">Payroll Summary</MenuItem>
                <MenuItem value="taxes">Tax Schedule</MenuItem>
                <MenuItem value="netpay">Net Pay Schedule</MenuItem>
              </MDInput>
              <MDButton color="success" onClick={queueExport} disabled={loading || !runId}>
                Queue Export
              </MDButton>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title="Recent Background Jobs"
            subtitle="This list auto-refreshes every few seconds."
          >
            <SimpleTable
              columns={[
                { label: "ID", key: "id" },
                { label: "Type", key: "type" },
                { label: "Month", key: "month" },
                { label: "Run", key: "payroll_run_id" },
                { label: "Report", key: "report_type" },
                {
                  label: "Status",
                  render: (row) => (
                    <Chip size="small" color={statusColor(row.status)} label={row.status} />
                  ),
                },
                {
                  label: "Progress",
                  render: (row) => (
                    <Stack spacing={0.5} sx={{ minWidth: 160 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Number(row.progress_percent || 0)}
                      />
                      <MDTypography variant="caption">
                        {row.progress_done || 0}/{row.progress_total || 0} (
                        {row.progress_percent || 0}%)
                      </MDTypography>
                    </Stack>
                  ),
                },
                { label: "Message", key: "message" },
                {
                  label: "Action",
                  render: (row) => (
                    <Stack direction="row" spacing={1}>
                      {row.status === "completed" && row.result_path ? (
                        <MDButton size="small" color="info" onClick={() => downloadJob(row)}>
                          Download
                        </MDButton>
                      ) : null}
                      {["queued", "processing"].includes(row.status) ? (
                        <MDButton size="small" color="warning" onClick={() => cancelJob(row)}>
                          Cancel
                        </MDButton>
                      ) : null}
                    </Stack>
                  ),
                },
              ]}
              rows={jobs}
            />
          </SectionCard>
        </Grid>
      </Grid>
    </PageShell>
  );
}
